import React, { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  useDisclosure,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { menuService } from "@/services/menuService";
import { ProductType } from "@/types/menu";

interface ProductTypeManagerProps {
  productId: number;
  onUpdate?: () => void;
}

export default function ProductTypeManager({
  productId,
  onUpdate,
}: ProductTypeManagerProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentType, setCurrentType] = useState<ProductType | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    price: 0,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    fetchProductTypes();
  }, [productId]);

  const fetchProductTypes = async () => {
    try {
      setLoading(true);
      const data = await menuService.getProductTypes(productId);

      setProductTypes(data);
    } catch (err) {
      console.error("Error fetching product types:", err);
      setError("Failed to load product types");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type?: ProductType) => {
    if (type) {
      setCurrentType(type);
      setFormData({
        name: type.name,
        price: type.price / 100, // Convert cents to dollars for display
      });
      setEditMode(true);
    } else {
      setCurrentType(null);
      setFormData({
        name: "",
        price: 0,
      });
      setEditMode(false);
    }
    setError("");
    onOpen();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: name === "price" ? parseFloat(value) || 0 : value,
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        setError("Name is required");

        return;
      }

      if (formData.price <= 0) {
        setError("Price must be greater than 0");

        return;
      }

      // Convert price to cents for API
      const priceInCents = Math.round(formData.price * 100);

      if (editMode && currentType) {
        await menuService.updateProductType(currentType.id, {
          name: formData.name,
          price: priceInCents,
        });
      } else {
        await menuService.createProductType({
          name: formData.name,
          price: priceInCents,
          product_id: productId,
        });
      }

      // Refresh data
      await fetchProductTypes();
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      console.error("Error saving product type:", err);
      setError("Failed to save product type");
    }
  };

  const handleDelete = async (typeId: number) => {
    try {
      await menuService.deleteProductType(typeId);
      await fetchProductTypes();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error deleting product type:", err);
      setError("Failed to delete product type");
    }
  };

  if (loading && productTypes.length === 0) {
    return (
      <div className="flex justify-center p-4">
        <Spinner size="lg" />
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price / 100); // Convert cents to dollars
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Product Types</h3>
        <Button
          color="primary"
          startContent={<Plus size={16} />}
          onPress={() => handleOpenModal()}
        >
          Add Type
        </Button>
      </div>

      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}

      {productTypes.length === 0 ? (
        <div className="text-center p-4 border rounded-md border-dashed">
          No product types found. Add a type to get started.
        </div>
      ) : (
        <Table aria-label="Product types table">
          <TableHeader>
            <TableColumn>NAME</TableColumn>
            <TableColumn>PRICE</TableColumn>
            <TableColumn className="text-right">ACTIONS</TableColumn>
          </TableHeader>
          <TableBody items={productTypes}>
            {(type) => (
              <TableRow key={type.id}>
                <TableCell>{type.name}</TableCell>
                <TableCell>{formatPrice(type.price)}</TableCell>
                <TableCell className="flex justify-end gap-2">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => handleOpenModal(type)}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Popover>
                    <PopoverTrigger>
                      <Button
                        isIconOnly
                        color="danger"
                        size="sm"
                        variant="light"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <div className="px-4 py-3 space-y-4">
                        <div className="text-small font-bold">
                          Are you sure you want to delete this type?
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button
                            color="danger"
                            variant="light"
                            onPress={() => handleDelete(type.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                {editMode ? "Edit Product Type" : "Add Product Type"}
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Input
                    label="Name"
                    name="name"
                    placeholder="Enter type name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  <Input
                    label="Price ($)"
                    min={0}
                    name="price"
                    placeholder="0.00"
                    startContent={
                      <div className="pointer-events-none flex items-center">
                        <span className="text-default-400 text-small">$</span>
                      </div>
                    }
                    step={0.01}
                    type="number"
                    value={formData.price.toString()}
                    onChange={handleInputChange}
                  />
                  {error && <div className="text-red-500 text-sm">{error}</div>}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleSubmit}>
                  {editMode ? "Update" : "Create"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
